"use client"

import { ExternalLink } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface WhatsappConnectNoticeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * What connecting a number already used in the WhatsApp Business app costs the
 * customer (Meta's "coexistence" mode). Informational only — opened from a link
 * on the connect tile; two of these are irreversible enough that finding out
 * afterwards is a support ticket.
 */
export function WhatsappConnectNoticeDialog({
  open,
  onOpenChange,
}: WhatsappConnectNoticeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connecting a WhatsApp Business app number</DialogTitle>
          <DialogDescription>
            You can connect a brand-new number, or one you already use in the WhatsApp Business
            app. Choosing the existing account keeps your number and your chats.
          </DialogDescription>
        </DialogHeader>

        {/* Scoped: none of this applies to a brand-new Cloud API number, and the
            same tile serves both intents. */}
        <p className="text-sm font-medium text-gray-900">
          If you connect a number you already use in the WhatsApp Business app:
        </p>
        <ul className="space-y-3 text-sm text-gray-600">
          <li>
            <span className="font-medium text-gray-900">Linked devices are unlinked once.</span>{" "}
            <span>All companion apps are disconnected during setup, and supported ones — including
            WhatsApp Web — can be linked again afterwards.</span>{" "}
            <span className="font-medium">WhatsApp for Windows</span> <span>and</span>{" "}
            <span className="font-medium">WhatsApp for WearOS</span> <span>are not supported and cannot
            be re-linked.</span>
          </li>
          <li>
            <span className="font-medium text-gray-900">Keep the Business app active.</span> <span>If the
            phone stays inactive for about 14 days, Meta disconnects the integration and you have
            to connect again.</span>
          </li>
          <li>
            <span className="font-medium text-gray-900">Some app features are turned off.</span>{" "}
            <span>Disappearing, view-once, and live-location messages are disabled in 1:1 chats, and you
            can no longer create broadcast lists.</span>
          </li>
          <li>
            <span className="font-medium text-gray-900">Messaging speed is capped.</span> <span>The
            number's throughput is fixed at 20 messages per second.</span>
          </li>
        </ul>

        <div className="space-y-1 text-sm text-gray-600">
          <p className="font-medium text-gray-900">Learn more in the official documentation:</p>
          <a
            href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline"
          >
            <span>Meta: Onboarding WhatsApp Business app users</span>
            <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href="https://faq.whatsapp.com/647349420360876"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline"
          >
            <span>WhatsApp Help Center: Linked devices on the Business app</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
